/** Display-format a phone string as (XXX) XXX-XXXX. Pass-through if not 10 digits. */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) {
    return '';
  }
  const digits = stripPhone(phone);
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/** Progressive input mask: keystrokes → (XXX) XXX-XXXX. */
export function formatPhoneInput(raw: string): string {
  const digits = stripPhone(raw).slice(0, 10);
  if (digits.length <= 3) {
    return `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Canonicalize phone input to E.164 while allowing incomplete input. */
export function canonicalizePhoneInput(raw: string): string {
  const value = raw.trim();
  const digits = value.replace(/\D/g, '');
  if (digits === '') {
    return '';
  }
  if (value.startsWith('+')) {
    return `+${digits.slice(0, 15)}`;
  }
  if (digits === '1') {
    return '+1';
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return `+1${digits.slice(0, 10)}`;
}

/** Canonicalize a US/Canada fax number and cap it at ten national digits. */
export function canonicalizeFaxNumberInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') {
    return '';
  }
  if (digits.startsWith('1')) {
    return `+${digits.slice(0, 11)}`;
  }
  return `+1${digits.slice(0, 10)}`;
}

/** Display a canonical fax number as +1 415 358 5998 while it is entered. */
export function formatFaxNumberInput(raw: string): string {
  const canonical = canonicalizeFaxNumberInput(raw);
  if (canonical === '') {
    return '';
  }
  const nationalDigits = canonical.slice(2);
  if (nationalDigits.length <= 3) {
    return `+1 ${nationalDigits}`;
  }
  if (nationalDigits.length <= 6) {
    return `+1 ${nationalDigits.slice(0, 3)} ${nationalDigits.slice(3)}`;
  }
  return `+1 ${nationalDigits.slice(0, 3)} ${nationalDigits.slice(3, 6)} ${nationalDigits.slice(6)}`;
}

/** Strip any formatted phone to its 10 digits (removes +1 country prefix). */
export function stripPhone(phone: string): string {
  let digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits;
}

/** NANP check: exactly 10 digits and area code does not start with 0 or 1. */
export function isValidNanpPhone(value: string): boolean {
  const digits = stripPhone(value);
  return digits.length === 10 && digits[0] !== '0' && digits[0] !== '1';
}
